import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { TableCell } from '@mui/material';
import {
  Typography,
  TableWrapperFooter,
  TableHeadCell,
  TableIconCell,
  TableDots,
  Pagination,
  Chip,
  SimpleTable,
} from '../../design-system';
import { DashboardHeader } from '../dashboard';
import { Filters } from './Filters';
import { useStore } from '../../context';
import { filterWithUserId, momentTextFormat, showToRoles } from '../../utils';
import {
  useStatesWithFilterCounter,
  usePaginationState,
  useStoragePricingsQuery,
} from '../../hooks';
import { saveCache } from '../../services';
import {
  storagePricingActionMenuMissingOptions,
  storagePricingActionMenuOptions,
  storagePricingDotsViewActions,
  StoragePricingStatusToLabels,
  StoragePricingStatusToChipColors,
  TABLE,
} from '../../const';
import { StoragePricingGetAllResponse } from '../../types';
import { i18n } from '../../i18n';

interface StoragePricingsPageProps {
  innerScroll?: boolean;
}

export const StoragePricingsPage = styled.div<StoragePricingsPageProps>`
  display: grid;
  grid-template-rows: max-content 1fr;
  grid-row-gap: 3rem;

  height: 100%;

  ${(props) =>
    props.innerScroll &&
    css`
      overflow: hidden;
    `}
`;

interface StoragePricingsHeaderProps {
  totalCount: number;
}

export const StoragePricingsHeader = ({ totalCount }: StoragePricingsHeaderProps) => {
  return (
    <DashboardHeader>
      <Typography variant={'textXL'} weight={'bold'}>
        {i18n.t('pages.storagePricing.title')}: {totalCount}
      </Typography>
    </DashboardHeader>
  );
};

interface StoragePricingsBodyProps {
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
}

export const StoragePricingsBody = ({ setTotalCount }: StoragePricingsBodyProps) => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const navigate = useNavigate();
  const { page, take, skip, handlePageChange } = usePaginationState();
  const [openDotsMenuIndex, setDotsMenuIndex] = useState<null | number>(null);
  const [states, setStates, filteredStates] = useStatesWithFilterCounter({
    country: '',
    city: '',
    yardId: '',
    clientId: '',
  });

  const { data = { data: [], totalCount: 0 }, isFetching } = useStoragePricingsQuery({
    ...filterWithUserId(filteredStates, user),
    take,
    skip,
  });

  useEffect(() => {
    if (isFetching) return;

    setTotalCount(data.totalCount);
  }, [isFetching]);

  const handleDotsToggle = (i: number) => {
    if (openDotsMenuIndex !== i) return setDotsMenuIndex(i);

    setDotsMenuIndex(null);
  };

  const handleFilterChange: typeof setStates = (...args) => {
    handlePageChange(null, 1);
    setStates(...args);
  };

  const handleDotsOptionSelect = async (
    row: StoragePricingGetAllResponse['data'][number],
    selectedOption: string,
    id: string,
  ) => {
    if (selectedOption === 'addPricing') {
      saveCache('storage-pricing-add', {
        yardName: row.yard.name,
        clientName: row.client.code,
      });
      navigate(`/dashboard/storage-pricing/add/${row.id}`);
    }
    if (selectedOption === 'viewPricing') {
      navigate(`/dashboard/storage-pricing/view/${id}`);
    }
  };

  const handleRowClick = (to: string, pricingStatus: string) => {
    if (pricingStatus === 'MISSING') return;
    if (openDotsMenuIndex !== null) return;
    navigate(to);
  };

  return (
    <SimpleTable
      data={data}
      filters={() => <Filters setStates={handleFilterChange} {...states} />}
      tableRowProps={(row, i) => ({
        key: 'storage-pricing' + i,
        onClick: () =>
          handleRowClick(
            `/dashboard/storage-pricing/view/${row.storagePricing.id}`,
            row.storagePricing.pricingStatus,
          ),
      })}
      columns={[
        {
          column: 1,
          head: () => <TableHeadCell align={'left'}>{i18n.t('label.actions')}</TableHeadCell>,
          body: (row, i) =>
            showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'], user) ||
            row.storagePricing.pricingStatus === 'ACTIVE' ? (
              <TableIconCell component={'th'} align={'left'}>
                <TableDots
                  open={openDotsMenuIndex === i}
                  onToggle={() => handleDotsToggle(i)}
                  options={
                    row.storagePricing.pricingStatus === 'MISSING'
                      ? showToRoles(
                          ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'],
                          user,
                        )
                        ? storagePricingActionMenuMissingOptions
                        : []
                      : showToRoles(
                          ['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'],
                          user,
                        )
                      ? storagePricingActionMenuOptions
                      : storagePricingDotsViewActions
                  }
                  onSelect={(selectedOption) => {
                    handleDotsOptionSelect(
                      row as StoragePricingGetAllResponse['data'][number],
                      selectedOption,
                      row.storagePricing.id,
                    );
                  }}
                />
              </TableIconCell>
            ) : (
              <TableIconCell component={'th'} align={'left'} />
            ),
        },
        {
          headText: () => i18n.t('label.clientName'),
          bodyText: (row) => row.client.code,
        },
        {
          headText: () => i18n.t('label.yardName'),
          bodyText: (row) => row.yard.name,
        },
        {
          headText: () => i18n.t('common.yardCompany'),
          bodyText: (row) => row.yard.yardCompany.legalName,
          isHidden: () => !showToRoles(['SUPER_ADMIN'], user),
        },
        {
          headText: () => i18n.t('label.pricingStatus'),
          body: (row) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={StoragePricingStatusToLabels[row.storagePricing.pricingStatus]}
                color={StoragePricingStatusToChipColors[row.storagePricing.pricingStatus]}
              />
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.effectiveDate'),
          bodyText: (row) =>
            row.storagePricing.activationDate
              ? momentTextFormat(row.storagePricing.activationDate, user)
              : '',
        },
        {
          headText: () => i18n.t('label.freeDays'),
          bodyText: (row) => row.storagePricing.freeDays + ' Days',
        },
      ]}
      footer={() => (
        <TableWrapperFooter>
          <Pagination
            count={Math.ceil(data.totalCount / TABLE.PAGE_MAX_SIZE)}
            page={page}
            onChange={handlePageChange}
          />
        </TableWrapperFooter>
      )}
    />
  );
};
